import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Divider,
    IconButton,
    useTheme,
    useMediaQuery,
    Button,
    Chip,
    Avatar,
    Grid,
    Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import GoogleMapEmbed from '../common/GoogleMapEmbed';
import { generateSponsorInvoice } from '../../utils/invoiceGenerator';
import {
    ProgressDot,
    ProgressLine,
    ExpandedSection,
    LeftColumn,
    RightColumn,
    TabButton,
    CompanyLogo,
    ServiceTitle,
    LocationText,
    SocialIcons,
    ProgressContainer,
    ProgressStep,
    ProgressContent,
    VenueActionButton,
    FormRow,
    ActionButton,
    StatusBadge,
    InvoiceContainer,
    InvoiceHeader,
    InvoiceRow,
    TotalRow,
    AmountText,
    ImageCarousel,
    CarouselNav,
    CarouselDot,
    CapacityTag
} from '../common/BookingsManagerStyles';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { getBackendUserUrl } from '../../utils/utils';
import ReadMoreText from '../common/ReadMoreText';

// Use a constant for alert color (info.main from theme or fallback)
const ALERT_COLOR = '#1976d2'; // fallback to a modern blue, can be replaced with theme.palette.info.main

const VenueSpaceCard = ({ space, selected, onSelect, imageUrl }) => {
    const features = space.features ? Object.keys(space.features).filter(k => space.features[k]) : [];
    const technology = space.technology ? Object.keys(space.technology).filter(k => space.technology[k]) : [];
    const idealEvents = space.idealEvents ? (Array.isArray(space.idealEvents) ? space.idealEvents : Object.keys(space.idealEvents)) : [];
    
    return (
        <Box sx={{
            minWidth: 350,
            maxWidth: 370,
            flex: '0 0 auto',
            p: 2,
            border: '1px solid #eee',
            borderRadius: 3,
            background: '#fafbfc',
            boxShadow: selected ? 4 : 1,
            position: 'relative',
            mr: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: { xs: 'auto', sm: 420, md: 440 },
            maxHeight: { xs: 'none', sm: 480, md: 500 },
            overflowY: { sm: 'auto', md: 'auto' },
        }}>
            <Box sx={{ flexGrow: 1 }}>
                <img src={imageUrl || space.imagesUrl?.[0] || space.coverPhoto || space.logo || space.otherPhotos?.[0]} alt={space.title} style={{ width: '100%', height: 160, borderRadius: 8, objectFit: 'cover', marginBottom: 12 }} />
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>{space.title}</Typography>
                <Typography variant="body2" color="primary.main" sx={{ mb: 1 }}>Starting at {space.startingPrice ? `$${space.startingPrice}` : 'N/A'}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Chip label={`${space.standingCapacity || 0} Standing`} color="primary" variant="outlined" />
                    <Chip label={`${space.seatedCapacity || 0} Seated`} color="primary" variant="outlined" />
                </Box>
                <ReadMoreText 
                    text={space.desc || ''}
                    maxLength={200}
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                />
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">Features</Typography>
                        {features.length > 0 ? features.slice(0, 5).map((key) => (
                            <Typography key={key} variant="body2">• {key}</Typography>
                        )) : <Typography variant="body2" color="text.secondary">None</Typography>}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">Technology</Typography>
                        {technology.length > 0 ? technology.slice(0, 5).map((key) => (
                            <Typography key={key} variant="body2">• {key}</Typography>
                        )) : <Typography variant="body2" color="text.secondary">None</Typography>}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">Ideal Events</Typography>
                        {idealEvents.length > 0 ? idealEvents.slice(0, 5).map((event) => (
                            <Typography key={event} variant="body2">• {event}</Typography>
                        )) : <Typography variant="body2" color="text.secondary">None</Typography>}
                    </Box>
                </Box>
            </Box>
            <Button
                variant={selected ? 'contained' : 'outlined'}
                color="primary"
                sx={{
                    mt: 2,
                    borderRadius: 2,
                    fontWeight: 600,
                    width: '100%',
                    backgroundColor: selected ? 'primary.main' : 'transparent',
                    color: selected ? '#fff' : 'primary.main',
                    borderColor: selected ? 'primary.main' : 'primary.main',
                    '&:hover': {
                        backgroundColor: selected ? 'primary.dark' : 'primary.light',
                        color: selected ? '#fff' : 'primary.main',
                    },
                }}
                onClick={() => {
                    onSelect();
                }}
            >
                {selected ? 'SELECTED' : 'SELECT'}
            </Button>
        </Box>
    );
};

const VenueDetailView = ({
    venue,
    event,
    venueRequest,
    setVenueRequest,
    onMinimize,
    selectedSpaces = [],
    onSelectSpace
}) => {
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const [selectedTab, setSelectedTab] = useState('SERVICE DETAILS');
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
    const fileInputRefs = useRef({});
    const [formStatuses, setFormStatuses] = useState({
        'Seasonal Specials': { status: 'available' },
        'Contract Form': { status: 'awaiting' },
        'Proof of Identity': { status: 'awaiting' },
        'Proof of Insurance': { status: 'awaiting' }
    });
    const [progressState, setProgressState] = useState({
        'request_sent': { completed: true, date: null },
        'request_accepted': { completed: false, date: null },
        'forms_completed': { completed: false, date: null },
        'forms_approved': { completed: false, date: null },
        'payment_sent': { completed: false, date: null },
    });

    const [selected, setSelected] = useState(false);

    useEffect(() => {
        const allFormsCompleted = Object.values(formStatuses).every(
            form => form.status === 'completed' || form.status === 'available'
        );
        if (allFormsCompleted && !progressState.forms_completed.completed) {
            setProgressState(prev => ({
                ...prev,
                'forms_completed': { completed: true, date: new Date().toISOString().split('T')[0] }
            }));
        }
    }, [formStatuses, progressState]);



    // Sync progress state with venueRequest status
    useEffect(() => {
        if (venueRequest?.status) {
            const now = new Date().toISOString().split('T')[0];
            switch(venueRequest.status) {
                case 'submitted':
                    setProgressState(prev => ({
                        ...prev,
                        'request_sent': { completed: true, date: now }
                    }));
                    break;
                case 'vendor_accepted':
                    setProgressState(prev => ({
                        ...prev,
                        'request_sent': { completed: true, date: now },
                        'request_accepted': { completed: true, date: now }
                    }));
                    break;
                case 'forms_uploaded':
                    setProgressState(prev => ({
                        ...prev,
                        'request_sent': { completed: true, date: now },
                        'request_accepted': { completed: true, date: now },
                        'forms_completed': { completed: true, date: now }
                    }));
                    break;
                case 'forms_approved':
                    setProgressState(prev => ({
                        ...prev,
                        'request_sent': { completed: true, date: now },
                        'request_accepted': { completed: true, date: now },
                        'forms_completed': { completed: true, date: now },
                        'forms_approved': { completed: true, date: now }
                    }));
                    break;
                case 'payment_complete':
                    setProgressState(prev => ({
                        ...prev,
                        'request_sent': { completed: true, date: now },
                        'request_accepted': { completed: true, date: now },
                        'forms_completed': { completed: true, date: now },
                        'forms_approved': { completed: true, date: now },
                        'payment_sent': { completed: true, date: now }
                    }));
                    break;
            }
        }
    }, [venueRequest?.status]);

    const handleTabChange = (tab) => setSelectedTab(tab);
    const handleFileUpload = (formId, e) => {
        if (e.target.files.length > 0) {
            const file = e.target.files[0];
            setFormStatuses(prev => ({
                ...prev,
                [formId]: { ...prev[formId], status: 'completed', file: file.name }
            }));
        }
    };
    const triggerFileInput = (formId) => {
        if (fileInputRefs.current[formId]) {
            fileInputRefs.current[formId].click();
        }
    };
    const getProgressStepLabel = (step) => {
        switch(step) {
            case 'request_sent': return 'Request sent';
            case 'request_accepted': return 'Request accepted';
            case 'forms_completed': return 'Forms completed';
            case 'forms_approved': return 'Forms approved';
            case 'payment_sent': return 'Payment sent';
            default: return step.replace('_', ' ');
        }
    };
    const goToNextImage = () => {
        if (venue.images && venue.images.length > 0) {
            setCurrentImageIndex((prev) => 
                prev === venue.images.length - 1 ? 0 : prev + 1
            );
        }
    };
    const goToImage = (index) => {
        setCurrentImageIndex(index);
    };
    const renderProgressTimeline = () => {
        const steps = Object.keys(progressState);
        return (
            <ProgressContainer>
                {steps.map((step, index) => (
                    <React.Fragment key={step}>
                        <ProgressStep>
                            <ProgressDot completed={progressState[step].completed}>
                                {progressState[step].completed && 
                                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                                }
                            </ProgressDot>
                            <ProgressContent>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        fontWeight: progressState[step].completed ? 500 : 400,
                                        color: progressState[step].completed ? 'text.primary' : 'text.secondary'
                                    }}
                                >
                                    {getProgressStepLabel(step)}
                                </Typography>
                                {progressState[step].date && (
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(progressState[step].date).toLocaleDateString()}
                                    </Typography>
                                )}
                            </ProgressContent>
                        </ProgressStep>
                        {index < steps.length - 1 && <ProgressLine completed={progressState[step].completed} />}
                    </React.Fragment>
                ))}
            </ProgressContainer>
        );
    };
    const renderServiceDetails = () => {
        // Minimal aggregation of features, technology, and ideal events from VenueSpaces
        const featuresSet = new Set();
        const technologySet = new Set();
        const idealEventsSet = new Set();
        if (Array.isArray(venue.VenueSpaces)) {
            venue.VenueSpaces.forEach(space => {
                // Features
                if (space.features && typeof space.features === 'object') {
                    Object.entries(space.features).forEach(([key, value]) => {
                        if (value) featuresSet.add(key);
                    });
                }
                // Technology
                if (space.technology && typeof space.technology === 'object') {
                    Object.entries(space.technology).forEach(([key, value]) => {
                        if (value) technologySet.add(key);
                    });
                }
                // Ideal Events
                if (space.idealEvents && typeof space.idealEvents === 'object') {
                    Object.keys(space.idealEvents).forEach(event => {
                        idealEventsSet.add(event);
                    });
                } else if (Array.isArray(space.idealEvents)) {
                    space.idealEvents.forEach(event => idealEventsSet.add(event));
                }
            });
        }
        // Fallback to top-level venue fields if set is empty
        if (featuresSet.size === 0 && venue.features && typeof venue.features === 'object') {
            Object.entries(venue.features).forEach(([key, value]) => {
                if (value) featuresSet.add(key);
            });
        }
        if (technologySet.size === 0 && venue.technology && typeof venue.technology === 'object') {
            Object.entries(venue.technology).forEach(([key, value]) => {
                if (value) technologySet.add(key);
            });
        }
        if (idealEventsSet.size === 0 && Array.isArray(venue.ideal_events)) {
            venue.ideal_events.forEach(event => idealEventsSet.add(event));
        }
        const features = Array.from(featuresSet);
        const technology = Array.from(technologySet);
        const idealEvents = Array.from(idealEventsSet);
        if (Array.isArray(venue.VenueSpaces) && venue.VenueSpaces.length > 0) {
            return (
                <Box sx={{ display: 'flex', overflowX: 'auto', gap: 0, pb: 2 }}>
                    {venue.VenueSpaces.map(space => (
                        <VenueSpaceCard
                            key={space.venueSpaceId}
                            space={space}
                            selected={!!(selectedSpaces && selectedSpaces.includes(space.venueSpaceId))}
                            onSelect={() => onSelectSpace && onSelectSpace(space)}
                            imageUrl={space.imagesUrl?.[0] || venue.logo || venue.coverPhoto}
                        />
                    ))}
                </Box>
            );
        }
        return (
            <Box>
                <ImageCarousel>
                    <img src={venue.coverPhoto || venue.otherPhotos?.[0]} alt={venue.title} />
                    <CarouselNav>
                        {venue.images?.map((_, index) => (
                            <CarouselDot 
                                key={index} 
                                active={index === currentImageIndex} 
                                onClick={() => goToImage(index)} 
                            />
                        ))}
                    </CarouselNav>
                </ImageCarousel>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip 
                        label={`${venue.seatedCapacity || 0} Seated`} 
                        color="primary" 
                        variant="outlined" 
                    />
                    <Chip 
                        label={`${venue.standingCapacity || 0} Standing`} 
                        color="primary" 
                        variant="outlined" 
                    />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                    <Box>
                        <Typography variant="h6" gutterBottom>Features</Typography>
                        <Box>
                            {features.length > 0 ? features.slice(0, 5).map((key) => (
                                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography>{key}</Typography>
                                </Box>
                            )) : <Typography color="text.secondary">No features listed</Typography>}
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="h6" gutterBottom>Technology</Typography>
                        <Box>
                            {technology.length > 0 ? technology.slice(0, 5).map((key) => (
                                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography>{key}</Typography>
                                </Box>
                            )) : <Typography color="text.secondary">No technology listed</Typography>}
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="h6" gutterBottom>Ideal Events</Typography>
                        <Box>
                            {idealEvents.length > 0 ? idealEvents.slice(0, 5).map((event) => (
                                <Box key={event} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography>{event}</Typography>
                                </Box>
                            )) : <Typography color="text.secondary">No ideal events listed</Typography>}
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    };
    const renderFormsDocuments = () => {
        const forms = [
            { id: 'Seasonal Specials', name: 'Seasonal Specials' },
            { id: 'Contract Form', name: 'Contract Form' },
            { id: 'Proof of Identity', name: 'Proof of Identity' },
            { id: 'Proof of Insurance', name: 'Proof of Insurance' },
        ];
        return forms.map(form => (
            <FormRow key={form.id}>
                <Typography variant="body1" fontWeight={500}>{form.name}</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ borderRadius: 2, minWidth: 100 }}
                        startIcon={<DownloadIcon fontSize="small" />}
                    >
                        Download
                    </Button>
                    {formStatuses[form.id].status === 'awaiting' ? (
                        <>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                sx={{ borderRadius: 2, minWidth: 100 }}
                                startIcon={<UploadIcon fontSize="small" />}
                                onClick={() => triggerFileInput(form.id)}
                            >
                                Submit
                            </Button>
                            <StatusBadge status="awaiting" sx={{ backgroundColor: ALERT_COLOR, color: '#fff' }}>
                                Awaiting completion
                            </StatusBadge>
                            <input
                                type="file"
                                ref={el => fileInputRefs.current[form.id] = el}
                                style={{ display: 'none' }}
                                onChange={(e) => handleFileUpload(form.id, e)}
                            />
                        </>
                    ) : formStatuses[form.id].status === 'completed' ? (
                        <StatusBadge status="completed">
                            Completed
                        </StatusBadge>
                    ) : null}
                </Box>
            </FormRow>
        ));
    };
    const renderInvoice = () => {
        // Mock invoice data - in real app this would come from the venue/service data
        const invoiceData = {
            sponsor: 'Sponsor - Capital One',
            items: [
                { name: 'Platinum Sponsorship', amount: 10000, type: 'positive' },
                { name: 'Local Sales Tax (7.5%)', amount: 750, type: 'negative' },
                { name: 'Processing Fee (3%)', amount: 300, type: 'negative' },
            ],
            total: 8950,
        };
        const handleDownloadInvoice = () => {
            // Use the reusable utility function to generate the invoice
            generateSponsorInvoice({
                name: invoiceData.sponsor,
                items: invoiceData.items,
                total: invoiceData.total
            }).then(result => {
                console.log(`Invoice ${result.invoiceNumber} generated successfully`);
            }).catch(error => {
                console.error('Error generating invoice:', error);
            });
        };
        return (
            <>
                <InvoiceContainer>
                    <InvoiceHeader>
                        <Typography variant="h6" gutterBottom>{invoiceData.sponsor}</Typography>
                        <Divider />
                    </InvoiceHeader>
                    {invoiceData.items.map((item, index) => (
                        <InvoiceRow key={index}>
                            <Typography variant="body1">{item.name}</Typography>
                            <AmountText 
                                variant="body1" 
                                color={item.type}
                            >
                                {item.type === 'positive' ? '+' : '-'}${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </AmountText>
                        </InvoiceRow>
                    ))}
                    <TotalRow>
                        <Typography variant="body1">Total Sponsorship Discount</Typography>
                        <AmountText variant="body1" color="positive">
                            +${invoiceData.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </AmountText>
                    </TotalRow>
                </InvoiceContainer>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ borderRadius: 2, minWidth: 140 }}
                        startIcon={<FileDownloadIcon fontSize="small" />}
                        onClick={handleDownloadInvoice}
                    >
                        Download Invoice
                    </Button>
                </Box>
            </>
        );
    };
    const handleRequestVenue = async () => {
        try {
            const backendUrl = getBackendUserUrl();
            const response = await fetch(backendUrl + '/event/venue_request/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    eventId: event.id,
                    venueId: venue.id,
                    venueSpaceId: venue.spaceId,
                    packageId: venue.packageId,
                    status: 'submitted'
                })
            });
            
            if (response.ok) {
                const responseData = await response.json();
                // Update local state to reflect the new venue request
                setVenueRequest(responseData.venueRequest);
                setIsRequestModalOpen(true);
                
                // Update progress state immediately
                const now = new Date().toISOString().split('T')[0];
                setProgressState(prev => ({
                    ...prev,
                    'request_sent': { completed: true, date: now }
                }));
            } else {
                const errorData = await response.json();
                console.error('Failed to submit venue request:', errorData);
                // TODO: Add toast notification for error
            }
        } catch (err) {
            console.error('Error submitting venue request:', err);
            // TODO: Add toast notification for error
        }
    };

    // Determine current status
    let status = 'predraft';
    if (venueRequest && venueRequest.status) {
        status = venueRequest.status;
    } else if (event && event.approvalStatus) {
        status = event.approvalStatus;
    }

    // UI for predraft
    if (status === 'predraft') {
        return (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, width: '100%', alignItems: 'stretch' }}>
                <LeftColumn sx={{ flex: 1, minWidth: 0, pr: { md: 3 } }}>
                    <CompanyLogo>
                        <img src={venue.logo || venue.images?.[0]?.url} alt={venue.title} />
                    </CompanyLogo>
                    <ServiceTitle>{venue.title}</ServiceTitle>
                    <Typography>
                        {venue.city}
                    </Typography>
                    <ReadMoreText 
                        text={venue.description || venue.desc || ''}
                        maxLength={250}
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <GoogleMapEmbed 
                        latitude={venue.latitude}
                        longitude={venue.longitude}
                        address={venue.address?.full}
                        height={160}
                    />
                    <Box sx={{ mt: 'auto', pt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            sx={{ borderRadius: 2, fontWeight: 600 }}
                            fullWidth
                            href={`/venue/${venue.venueId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            VISIT VENUE PAGE
                        </Button>
                    </Box>
                </LeftColumn>
                <RightColumn sx={{ flex: 2, minWidth: 0 }}>
                    {renderServiceDetails()}
                </RightColumn>
            </Box>
        );
    }
    // UI for draft
    if (status === 'draft') {
        return (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, width: '100%', alignItems: 'stretch' }}>
                {/* <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bgcolor: 'primary.light', color: 'primary.main', p: 2, borderRadius: 2, mb: 2 }}>
                    <Typography fontWeight={600}>
                        ACTION REQUIRED: Request Venue
                    </Typography>
                    <Typography variant="body2">
                        Venue must be confirmed to launch your event. Once the venue is confirmed, requests can be sent to other service providers.
                    </Typography>
                </Box> */}
                <LeftColumn sx={{ flex: 1, minWidth: 0, pr: { md: 3 }, mt: 8, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                    <CompanyLogo>
                        <img src={venue.logo || venue.images?.[0]?.url} alt={venue.title} />
                    </CompanyLogo>
                    <ServiceTitle>{venue.title}</ServiceTitle>
                    <LocationText>
                        {venue.address?.neighborhood}, {venue.address?.city}
                    </LocationText>
                    <GoogleMapEmbed 
                        latitude={venue.latitude}
                        longitude={venue.longitude}
                        address={venue.address?.full}
                        height={200}
                    />
                    <Box sx={{ mt: 'auto', pt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            sx={{ borderRadius: 2, fontWeight: 600 }}
                            fullWidth
                            href={`/venue/${venue.venueId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            VISIT VENUE PAGE
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{ borderRadius: 2, fontWeight: 600 }}
                            fullWidth
                            onClick={handleRequestVenue}
                            startIcon={<EventAvailableIcon />}
                        >
                            REQUEST VENUE
                        </Button>
                    </Box>
                </LeftColumn>
                <RightColumn sx={{ flex: 2, minWidth: 0, mt: 8 }}>
                    {renderServiceDetails()}
                </RightColumn>
            </Box>
        );
    }
    // UI for submitted and later
    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, width: '100%', alignItems: 'stretch' }}>
            <LeftColumn sx={{ flex: 1, minWidth: 0, pr: { md: 3 } }}>
                <CompanyLogo>
                    <img src={venue.logo || venue.images?.[0]?.url} alt={venue.title} />
                </CompanyLogo>
                <ServiceTitle>{venue.title}</ServiceTitle>
                <LocationText>
                    {venue.address?.neighborhood}, {venue.address?.city}
                </LocationText>
                <Typography variant="h6" gutterBottom>
                    Booking Progress
                </Typography>
                {renderProgressTimeline()}
                <GoogleMapEmbed 
                    latitude={venue.latitude}
                    longitude={venue.longitude}
                    address={venue.address?.full}
                    height={200}
                />
                <Box sx={{ mt: 'auto', pt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ borderRadius: 2, fontWeight: 600 }}
                        fullWidth
                        startIcon={<MailOutlineIcon />}
                    >
                        MESSAGE VENUE
                    </Button>
                </Box>
            </LeftColumn>
            <RightColumn sx={{ flex: 2, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TabButton
                            selected={selectedTab === 'SERVICE DETAILS'}
                            onClick={() => handleTabChange('SERVICE DETAILS')}
                        >
                            Service details
                        </TabButton>
                        <TabButton
                            selected={selectedTab === 'INVOICE & PAYMENT'}
                            onClick={() => handleTabChange('INVOICE & PAYMENT')}
                        >
                            Invoice & payment
                        </TabButton>
                        <TabButton
                            selected={selectedTab === 'FORMS & DOCUMENTS'}
                            onClick={() => handleTabChange('FORMS & DOCUMENTS')}
                        >
                            Forms & documents
                        </TabButton>
                    </Box>
                </Box>
                {selectedTab === 'SERVICE DETAILS' && renderServiceDetails()}
                {selectedTab === 'INVOICE & PAYMENT' && (
                    <Box>
                        {renderInvoice()}
                    </Box>
                )}
                {selectedTab === 'FORMS & DOCUMENTS' && (
                    <Box>
                        {renderFormsDocuments()}
                    </Box>
                )}
            </RightColumn>
        </Box>
    );
};

export default VenueDetailView; 